/**
 * MBA Lab — static prompt and label content for the Peer Evaluation Drafter.
 * Pure data, no functions, matching listening-prompts.js and
 * readiness-dimensions.js.
 *
 * Every prompt here operationalizes one specific move from this site's own
 * already-published article, Content/Blog/peer-evaluations-mba-teams.md -
 * it is not a generic feedback framework retrofitted onto that article. The
 * mapping, section by section:
 *
 *   "Say what actually worked, specifically" -> STRENGTH_FIELDS (the article's
 *      complaint is that "great teammate" tells nobody anything, so the two
 *      fields are the specific act and what it made possible).
 *   "Write about behaviour, not character"   -> GAP_FIELDS, which are the
 *      article's own three rules in its own order: name the observable thing,
 *      name the consequence, say what good would have looked like.
 *   "The proportionality question"           -> PROPORTIONALITY_QUESTION and
 *      RAISED_OPTIONS, plus RAISED_ACKNOWLEDGMENT_SEED, which is the article's
 *      own suggested sentence for the case where you did not raise it.
 *   "What most people get wrong"             -> CALIBRATION_CHECKS, the
 *      self-checks that catch inflation and the anonymous vent before they
 *      reach the form.
 *
 * There is deliberately no overall-summary prompt anywhere in this file. A
 * free-form summary box is exactly where the article's "great collaborator"
 * boilerplate lives, and adding one would undo the rest of the tool.
 */

export const TEAMMATE_LABEL_FIELD = Object.freeze({
  key: 'teammateLabel',
  label: 'What do you call this person?',
  help: 'An initial or a first name is plenty - this is only a tab label so you can tell your drafts apart.',
});

export const STRENGTH_FIELDS = Object.freeze([
  {
    key: 'strengthObservation',
    label: 'What did they actually do?',
    help: '"Great teammate" tells nobody anything. Name the specific thing you watched happen, and roughly when.',
  },
  {
    key: 'strengthImpact',
    label: 'What did that make possible for the team?',
    help: 'People are frequently unaware of which of their habits are valuable, especially the quiet ones.',
  },
]);

export const GAP_FIELDS = Object.freeze([
  {
    key: 'gapObservation',
    label: 'Name the observable thing, not the inferred motive.',
    help: 'You saw a missed deadline. You did not see "didn\'t care."',
  },
  {
    key: 'gapCost',
    label: 'Name the consequence.',
    help: 'Feedback without cost attached is easy to file away as someone\'s preference.',
  },
  {
    key: 'gapAlternative',
    label: 'Say what good would have looked like.',
    help: '"Flagging on Sunday that Tuesday would slip" is a specific alternative.',
  },
]);

export const PROPORTIONALITY_QUESTION = 'Was this raised during the project?';

export const PROPORTIONALITY_HELP =
  'Feedback that appears for the first time in a final evaluation is an ambush, and it is the main '
  + 'reason peer review breeds resentment. If it mattered enough to write down at the end, it '
  + 'mattered enough to say in week four.';

export const RAISED_OPTIONS = Object.freeze([
  { value: 'yes', label: 'Yes - I raised it while it could still be fixed' },
  { value: 'no', label: 'No - this evaluation is the first they will hear of it' },
  { value: 'nothing-hard', label: 'There is nothing hard in this evaluation' },
]);

export const RAISED_NOTE_LABEL = 'Acknowledge that in your own words';

/**
 * The article's own suggested sentence, quoted from it directly. Seeded into
 * the acknowledgment field the moment someone answers "no" - and deliberately
 * NOT counted as their content until they change it, because a sentence this
 * tool wrote is not the user owning their part (see peer-eval-model.js's
 * isUntouchedSeed / isProportionalitySettled).
 */
export const RAISED_ACKNOWLEDGMENT_SEED = 'I should have flagged this in week four rather than here.';

/**
 * Five self-checks, run against the draft the user is looking at. These are
 * attestations about one specific piece of text, which is a large part of why
 * this whole tool is session-only: a stored "yes, this passes the face test"
 * means nothing once the text it referred to has been stripped out.
 */
export const CALIBRATION_CHECKS = Object.freeze([
  {
    key: 'recency',
    label: 'Does this describe the whole term, not just the last two weeks?',
  },
  {
    key: 'halo',
    label: 'Is each point its own observation, not one strong impression spread across all of them?',
  },
  {
    key: 'retaliation',
    label: 'Is none of this a response to what I think they wrote about me?',
  },
  {
    key: 'faceTest',
    label: 'Would I say this to them directly, in these words?',
  },
  {
    key: 'numbersMatchWords',
    label: 'Do the scores I am about to give match what I have written here?',
  },
]);

/** Section headings used by buildPlainText(). Plain text, no markdown. */
export const EXPORT_HEADINGS = Object.freeze({
  strength: 'What worked',
  gap: 'What did not',
  alternative: 'What good would have looked like:',
  raised: 'Raised during the project:',
});

export const EXPORT_TITLE = 'Peer evaluation notes';

export const EXPORT_EMPTY_NOTE = 'Nothing drafted yet - add a teammate and describe what you saw.';
